import React, {useCallback, useMemo, useRef, useState} from 'react';
import ReactTooltip from "react-tooltip";
import Autocomplete from "react-autocomplete";
import PropTypes from "prop-types";
import {IngredientBox} from "./IngredientBox";
import {SmallIngredientBox} from "./SmallIngredientBox";
import {parseTask} from "../parser/TaskParser";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";

function Omnibar(props) {
    const [text, setText] = useState("")
    const [item, setItem] = useState(null)
    const [cachedResults, setCachedResults] = useState([])
    const [_selected, setSelected] = useState(false)
    const [error, setError] = useState(null)
    const errorTooltip = useRef(null)
    const messageTooltip = useRef(null)
    const bar = useRef(null)

    const validate = useCallback((value) => {
        if (value === "") {
            setError(null)
            return true
        }
        if (props.onValidate) {
            try {
                props.onValidate(value)
                setError(null)
                return true
            } catch (e) {
                setError(e.message)
                return false
            }
        }
        try {
            parseTask(value.split(' '))
            setError(null)
            return true
        } catch (e) {
            setError(e.message)
            return false
        }
    }, [props])
    const recommendations = useMemo(() => {
        const searchtext = text.trim().toLowerCase()
        const recommendations = []

        for (let res of cachedResults) {
            recommendations.push(res)
            if (recommendations.length >= 20) {
                break
            }
        }

        if (props.recommendations) {
            for (let rec of props.recommendations) {
                if (rec.name.toLowerCase().includes(searchtext)) {
                    recommendations.push(rec)
                }
                if (recommendations.length >= 20) {
                    break
                }
            }
        }
        return recommendations
    }, [text, cachedResults, props.recommendations])

    const handleKey = event => {
        ReactTooltip.hide(messageTooltip.current)
        ReactTooltip.hide(errorTooltip.current)
        if (event.key === 'Enter' && text !== "") {
            if (validate(text)) {
                submit()
            }
        } else if (event.key === 'Backspace') {
            setSelected(false)
        } else if (event.keyCode === 27 && props.onEscape) {
            props.onEscape()
        }
    }

    const submit = () => {
        if (!validate(text)) {
            return
        }
        try {
            props.onSubmit(text, item, showTooltip)
            setText("")
            setItem(null)
            setCachedResults([])
        } catch (e) {
            setError(e.toString())
        }
    }

    const showTooltip = (msg, type = "error") => {
        let tooltip = type === "error" ? errorTooltip : messageTooltip
        if (tooltip.current && msg) {
            tooltip.current.setAttribute("data-tip", msg)
            ReactTooltip.show(tooltip.current)
        }
    }

    return (<div
        className={props.resizeable ? "form-inline mr-auto w-100 navbar-search" : "d-none d-sm-inline-block form-inline mr-auto ml-md-3 my-2 my-md-0 mw-100 navbar-search"}
    >
        <div className="input-group" style={{width: 100 + '%', marginTop: 5 + 'px'}}>
            <Autocomplete
                ref={(input) => {
                    bar.current = input;
                }}
                style={{width: 100 + '%'}}
                getItemValue={(item) => {
                    if (item.name) {
                        return item.name;
                    } else if (item.product_name) {
                        return item.product_name;
                    } else {
                        alert("Error, please check the logs.")
                        return "Error, please check the logs."
                    }
                }}
                items={recommendations}
                wrapperProps={{
                    className: "bg-light", style: {
                        position: "relative",
                        flex: "1 1 auto",
                        width: 1 + "%",
                        minWidth: 0,
                        marginBottom: 5 + 'px',
                        marginTop: 5 + 'px',
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0
                    }
                }}
                isItemSelectable={function () {
                    return true
                }}
                menuStyle={{
                    fontSize: '90%', position: 'absolute', zIndex: 100, maxHeight: '50%', top: 40, left: 0
                }}
                renderItem={(item, isHighlighted) => <div
                    key={(item["_id"] && item["_id"]["$oid"]) ? item["_id"]["$oid"] : item["db_id"]}>
                    {!props.smallRecommendations ?
                        <IngredientBox highlighted={isHighlighted}
                                       onEdit={() => {
                                           props.showModal()
                                       }
                                       }
                                       item={item}/> : <SmallIngredientBox highlighted={isHighlighted}
                                                                           item={item}/>
                    }
                </div>}
                renderInput={props => <input autoFocus={props.autofocus} placeholder={props.placeholder}
                                             style={{
                                                 width: 100 + '%', ...props.inputstyle,
                                                 border: error ? '1px solid red' : 'none'
                                             }}
                                             className="form-control bg-light" {...props}
                                             onSubmit={(event) => {
                                                 event.preventDefault()
                                             }}
                                             onKeyDown={(event) => {
                                                 handleKey(event);
                                                 props.onKeyDown(event);
                                             }}/>}
                value={text}
                onChange={(e) => {
                    const val = e.target.value
                    setText(val)
                    if (!validate(val)) {
                        showTooltip(val.split(' ').length > 0 ? error : null, "error")
                    } else {
                        ReactTooltip.hide(errorTooltip.current)
                    }
                }}
                onSelect={(e, item) => {
                    setSelected(true)
                    setText(e)
                    setItem(item)
                    validate(e)
                    if (bar.current) {
                        bar.current.focus()
                    }
                    if (props.onSelect) {
                        props.onSelect(item)
                    }
                }}
            />
            {!props.hidebutton ? <div className="input-group-append">
                <button className="btn btn-primary" disabled={error !== null} onClick={(event) => {
                    event.preventDefault()
                    if (text === "") {
                        if (props.onEmptySubmit) {
                            props.onEmptySubmit()
                        }
                    } else {
                        submit();
                    }
                }}>
                    <FontAwesomeIcon icon={solid('plus')} className="fa-sm"/>
                </button>
            </div> : ""}
        </div>
        <ReactTooltip id={"error"} place={"bottom"} type={"error"} effect="solid"/>
        <ReactTooltip id={"info"} place={"bottom"} type={"info"} effect="solid"/>
        <p ref={ref => errorTooltip.current = ref} data-for={"error"} data-tip="React-tooltip"></p>
        <p ref={ref => messageTooltip.current = ref} data-for={"info"} data-tip="React-tooltip"></p>
    </div>);
}

Omnibar.propTypes = {
    placeholder: PropTypes.string,
    recommendations: PropTypes.array,
    onSubmit: PropTypes.func,
    onEmptySubmit: PropTypes.func
};

export default Omnibar;